import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { useToast } from './useToast';

export const useDeepLinks = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  useEffect(() => {
    // Function to handle deep links
    const handleDeepLink = async (url: string) => {
      try {
        console.log('Deep link received:', url);
        
        // Parse the URL
        const parsedUrl = new URL(url);
        
        // Check if it's a password reset link
        if (parsedUrl.pathname === '/reset-password' || parsedUrl.pathname.includes('reset-password')) {
          // Extract tokens from query parameters or hash
          const params = new URLSearchParams(parsedUrl.search || parsedUrl.hash.substring(1));
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          const type = params.get('type');
          
          if ((type === 'recovery' || type === 'signup') && accessToken && refreshToken) {
            // Set the session with the tokens
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });
            
            if (error) throw error;
            
            // Navigate to the reset password page
            navigate('/reset-password');
            showToast('You can now reset your password', 'success');
            return;
          }
        }
      } catch (error) {
        console.error('Error handling deep link:', error);
        showToast('Failed to process the link', 'error');
      }
    };

    // For web testing - handle URL hash or search params
    if (window.location.hash && window.location.hash.includes('access_token')) {
      const hash = window.location.hash.substring(1);
      handleDeepLink(`${window.location.origin}/reset-password?${hash}`);
    } else if (window.location.search && window.location.search.includes('access_token')) {
      handleDeepLink(`${window.location.origin}${window.location.pathname}${window.location.search}`);
    }

    // For mobile apps - this would be connected to the native deep link handler
    // This is a placeholder for the actual implementation in a real mobile app
    const setupDeepLinkHandler = () => {
      // Check if we're in a Capacitor environment
      if (typeof (window as any).Capacitor !== 'undefined') {
        const { App } = (window as any).Capacitor.Plugins;
        
        // Listen for deep link events
        App.addListener('appUrlOpen', (data: { url: string }) => {
          console.log('App URL opened:', data.url);
          handleDeepLink(data.url);
        });
      }
    };

    setupDeepLinkHandler();

    return () => {
      // Cleanup listeners if needed
      if (typeof (window as any).Capacitor !== 'undefined') {
        const { App } = (window as any).Capacitor.Plugins;
        App.removeAllListeners();
      }
    };
  }, [navigate, showToast]);
};